import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Extension } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import { ReactRenderer } from '@tiptap/react';
import {
  LuHeading1, LuHeading2, LuHeading3, LuList, LuListOrdered, LuListChecks,
  LuTable, LuImage, LuQuote, LuCode, LuMinus, LuYoutube, LuFileCode, LuPilcrow, LuFileStack,
} from 'react-icons/lu';

const SLASH_ITEMS = [
  {
    title: 'Título 1', description: 'Título grande de seção', icon: LuHeading1, keywords: ['h1', 'titulo'],
    command: ({ editor, range }) => editor.chain().focus().deleteRange(range).setNode('heading', { level: 1 }).run(),
  },
  {
    title: 'Título 2', description: 'Subtítulo médio', icon: LuHeading2, keywords: ['h2', 'subtitulo'],
    command: ({ editor, range }) => editor.chain().focus().deleteRange(range).setNode('heading', { level: 2 }).run(),
  },
  {
    title: 'Título 3', description: 'Subtítulo pequeno', icon: LuHeading3, keywords: ['h3'],
    command: ({ editor, range }) => editor.chain().focus().deleteRange(range).setNode('heading', { level: 3 }).run(),
  },
  {
    title: 'Parágrafo', description: 'Texto normal', icon: LuPilcrow, keywords: ['texto', 'p'],
    command: ({ editor, range }) => editor.chain().focus().deleteRange(range).setParagraph().run(),
  },
  {
    title: 'Lista', description: 'Lista com marcadores', icon: LuList, keywords: ['bullet', 'lista'],
    command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleBulletList().run(),
  },
  {
    title: 'Lista numerada', description: 'Lista com números', icon: LuListOrdered, keywords: ['ordered', 'numero'],
    command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
  },
  {
    title: 'Checklist', description: 'Lista de tarefas com checkbox', icon: LuListChecks, keywords: ['task', 'todo', 'check'],
    command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleTaskList().run(),
  },
  {
    title: 'Tabela', description: 'Inserir tabela 3x3', icon: LuTable, keywords: ['table', 'tabela'],
    command: ({ editor, range }) => editor.chain().focus().deleteRange(range).insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
  },
  {
    title: 'Imagem', description: 'Enviar uma imagem', icon: LuImage, keywords: ['img', 'foto', 'imagem'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).run();
      editor.wikiRequestImage?.();
    },
  },
  {
    title: 'Citação', description: 'Bloco de citação', icon: LuQuote, keywords: ['quote', 'citacao'],
    command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleBlockquote().run(),
  },
  {
    title: 'Código', description: 'Bloco de código', icon: LuCode, keywords: ['code', 'codigo'],
    command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
  },
  {
    title: 'HTML', description: 'Bloco de HTML personalizado', icon: LuFileCode, keywords: ['html', 'embed'],
    command: ({ editor, range }) => editor.chain().focus().deleteRange(range).insertHtmlBlock().run(),
  },
  {
    title: 'YouTube', description: 'Incorporar vídeo do YouTube', icon: LuYoutube, keywords: ['video', 'youtube'],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).run();
      editor.wikiRequestYoutube?.();
    },
  },
  {
    title: 'Quebra de página', description: 'Começa uma página nova no documento', icon: LuFileStack, keywords: ['pagina', 'page', 'quebra', 'folha'],
    command: ({ editor, range }) => editor.chain().focus().deleteRange(range).insertPageBreak().run(),
  },
  {
    title: 'Separador', description: 'Linha horizontal', icon: LuMinus, keywords: ['hr', 'divisor', 'linha'],
    command: ({ editor, range }) => editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
  },
];

const SlashMenuList = forwardRef(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => setSelectedIndex(0), [items]);

  const selectItem = (index) => {
    const item = items[index];
    if (item) command(item);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((selectedIndex + items.length - 1) % items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((selectedIndex + 1) % items.length);
        return true;
      }
      if (event.key === 'Enter') {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (!items.length) {
    return <div className="wiki-slash-menu"><div className="wiki-slash-empty">Nenhum bloco encontrado</div></div>;
  }

  return (
    <div className="wiki-slash-menu">
      {items.map((item, index) => {
        const IconComp = item.icon;
        return (
          <button
            type="button"
            key={item.title}
            className={`wiki-slash-item ${index === selectedIndex ? 'is-selected' : ''}`}
            onClick={() => selectItem(index)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <span className="wiki-slash-icon"><IconComp /></span>
            <span>
              <span className="wiki-slash-title">{item.title}</span>
              <span className="wiki-slash-desc">{item.description}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
});

const positionPopup = (popup, clientRect) => {
  const rect = typeof clientRect === 'function' ? clientRect() : null;
  if (!rect) return;
  const menuHeight = Math.min(340, popup.firstChild?.scrollHeight || 340);
  const spaceBelow = window.innerHeight - rect.bottom;
  popup.style.position = 'absolute';
  popup.style.left = `${rect.left + window.scrollX}px`;
  if (spaceBelow < menuHeight + 12) {
    popup.style.top = `${rect.top + window.scrollY - menuHeight - 6}px`;
  } else {
    popup.style.top = `${rect.bottom + window.scrollY + 6}px`;
  }
};

export const SlashCommands = Extension.create({
  name: 'slashCommands',

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        char: '/',
        allowSpaces: false,
        command: ({ editor, range, props }) => props.command({ editor, range }),
        items: ({ query }) => {
          const q = (query || '').toLowerCase();
          return SLASH_ITEMS.filter(
            (item) =>
              item.title.toLowerCase().includes(q) ||
              (item.keywords || []).some((k) => k.includes(q))
          );
        },
        render: () => {
          let component;
          let popup;

          return {
            onStart: (props) => {
              component = new ReactRenderer(SlashMenuList, { props, editor: props.editor });
              popup = document.createElement('div');
              popup.className = 'wiki-slash-popup';
              popup.appendChild(component.element);
              document.body.appendChild(popup);
              positionPopup(popup, props.clientRect);
            },
            onUpdate: (props) => {
              component.updateProps(props);
              positionPopup(popup, props.clientRect);
            },
            onKeyDown: (props) => {
              if (props.event.key === 'Escape') {
                popup.remove();
                return true;
              }
              return component.ref?.onKeyDown(props) || false;
            },
            onExit: () => {
              popup.remove();
              component.destroy();
            },
          };
        },
      }),
    ];
  },
});
